/**
 * Drag-and-drop target resolution of the course builder (#526): lessons can
 * be dropped into other sections, not only reordered within their own.
 * Draggable ids are prefixed so sections and lessons share one DndContext.
 */
export const SECTION_PREFIX = 'section-';
export const LESSON_PREFIX = 'lesson-';

export const sectionDndId = (id: number): string => `${SECTION_PREFIX}${id}`;
export const lessonDndId = (id: number): string => `${LESSON_PREFIX}${id}`;

export const parseDndId = (id: string | number): { kind: 'section' | 'lesson'; id: number } | null => {
    const raw = String(id);
    if (raw.startsWith(SECTION_PREFIX)) return { kind: 'section', id: Number(raw.slice(SECTION_PREFIX.length)) };
    if (raw.startsWith(LESSON_PREFIX)) return { kind: 'lesson', id: Number(raw.slice(LESSON_PREFIX.length)) };
    return null;
};

export interface DropLesson {
    id: number;
    sectionId: number | null;
}

export type LessonDrop =
    | { type: 'reorder'; sectionId: number; orderedIds: number[] }
    | { type: 'move'; lessonId: number; targetSectionId: number; position: number };

/**
 * @param lessons all lessons of the course in display order
 * @returns what to send to the API, or null when nothing changes
 */
export const resolveLessonDrop = (lessons: DropLesson[], activeId: string | number, overId: string | number): LessonDrop | null => {
    const active = parseDndId(activeId);
    const over = parseDndId(overId);
    if (!active || !over || active.kind !== 'lesson') return null;
    const dragged = lessons.find((l) => l.id === active.id);
    if (!dragged) return null;

    let targetSectionId: number | null;
    let overIndex: number | null = null; // index among the target section's lessons
    if (over.kind === 'section') {
        targetSectionId = over.id;
    } else {
        const overLesson = lessons.find((l) => l.id === over.id);
        if (!overLesson || overLesson.sectionId === null) return null;
        targetSectionId = overLesson.sectionId;
        overIndex = lessons.filter((l) => l.sectionId === targetSectionId).findIndex((l) => l.id === over.id);
    }

    const targetIds = lessons.filter((l) => l.sectionId === targetSectionId).map((l) => l.id);
    if (dragged.sectionId === targetSectionId) {
        if (overIndex === null || active.id === over.id) return null;
        const from = targetIds.indexOf(active.id);
        const ordered = [...targetIds];
        ordered.splice(from, 1);
        ordered.splice(overIndex, 0, active.id);
        return { type: 'reorder', sectionId: targetSectionId, orderedIds: ordered };
    }

    // Into another section: before the lesson it was dropped on, or at the end when dropped on the section itself.
    return { type: 'move', lessonId: active.id, targetSectionId: targetSectionId as number, position: overIndex ?? targetIds.length };
};
